import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainer } from '@/components/calculator/FieldExplainer';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Select } from '@/components/ui/select';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getCompoundInterestFieldExplainers } from '@/content/field-explainers/compound-interest';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateCompoundInterest, type CompoundInterestInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  formatPercent,
  parseContractState
} from './shared';

type CompoundState = {
  principal: number;
  monthlyContribution: number;
  interestRate: number;
  years: number;
  compoundFrequency: string;
};

const defaults: CompoundState = {
  principal: 25000,
  monthlyContribution: 900,
  interestRate: 7,
  years: 25,
  compoundFrequency: '12'
};

const validFrequencies = new Set(['1', '2', '4', '12', '365']);

// A hand-edited share URL can carry any string as the frequency. The engine
// falls back to monthly for unparseable values, so the page state must land on
// the same value or the select, share links, and results silently disagree.
function normalizeCompoundState(state: CompoundState): CompoundState {
  return validFrequencies.has(state.compoundFrequency) ? state : { ...state, compoundFrequency: '12' };
}

const compoundFrequencyOptions = [
  { value: '1', label: 'Annually' },
  { value: '2', label: 'Semi-annually' },
  { value: '4', label: 'Quarterly' },
  { value: '12', label: 'Monthly' },
  { value: '365', label: 'Daily' }
];

/** Mirrors normalizeCompoundFrequency in assets/js/calculator-engines/compound-interest-engine.js. */
function resolveCompoundFrequency(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 12;
}

const chartConfig = {
  endBalance: {
    label: 'End balance',
    color: 'var(--chart-1)'
  },
  contributions: {
    label: 'Contributions to date',
    color: 'var(--chart-2)'
  },
  interest: {
    label: 'Interest to date',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

type Props = {
  route: RouteDefinition;
};

function toEngineInput(state: CompoundState): CompoundInterestInput {
  return {
    principal: state.principal,
    monthlyContribution: state.monthlyContribution,
    interestRate: state.interestRate,
    years: state.years,
    compoundFrequency: state.compoundFrequency
  };
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function CompoundInterestCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<CompoundState>(() => normalizeCompoundState(parseContractState('compoundInterest', defaults)));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateCompoundInterest(toEngineInput(state)), [state]);
  const growthRatio = result.totalContributions > 0 ? result.totalInterest / result.totalContributions : 0;

  const explainers = useMemo(() => {
    const frequency = resolveCompoundFrequency(state.compoundFrequency);
    const entries = getCompoundInterestFieldExplainers({
      input: {
        principal: state.principal,
        monthlyContribution: state.monthlyContribution,
        interestRate: state.interestRate,
        years: state.years,
        compoundFrequency: frequency,
        compoundFrequencyLabel:
          compoundFrequencyOptions.find((option) => option.value === String(frequency))?.label ?? `${frequency} times a year`
      },
      result: {
        finalBalance: result.finalBalance,
        totalContributions: result.totalContributions,
        totalInterest: result.totalInterest,
        ruleOf72: result.ruleOf72,
        firstYearInterest: result.yearlyData[0]?.interest ?? 0
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  const chartData = useMemo(() => {
    // Cumulative running totals so all three lines share the end balance's
    // frame; the per-year flows stay in the projection table below.
    let contributionsToDate = state.principal;
    let interestToDate = 0;

    return result.yearlyData.map((row) => {
      contributionsToDate += row.contributions;
      interestToDate += row.interest;
      return {
        year: row.year,
        endBalance: row.endBalance,
        contributions: contributionsToDate,
        interest: interestToDate
      };
    });
  }, [result.yearlyData, state.principal]);

  const shareState = {
    principal: state.principal,
    monthlyContribution: state.monthlyContribution,
    interestRate: state.interestRate,
    years: state.years,
    compoundFrequency: state.compoundFrequency
  };

  return (
    <AppLayout showRouteHowItWorks={false}>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Compound Interest Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Project growth from principal, recurring contributions, and compounding frequency with full yearly detail.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel
          title="Compound growth assumptions"
          description="Inputs map directly to the compound-interest engine and include full share/export support."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="compoundPrincipal" label="Starting principal" value={state.principal} min={0} step={1000} prefix="$" explainer={explainers.byId.get('principal')} onChange={(value) => setState((prev) => ({ ...prev, principal: value }))} />
            <NumberField id="compoundMonthly" label="Monthly contribution" value={state.monthlyContribution} min={0} step={50} prefix="$" explainer={explainers.byId.get('monthlyContribution')} onChange={(value) => setState((prev) => ({ ...prev, monthlyContribution: value }))} />
            <NumberField id="compoundRate" label="Annual rate" value={state.interestRate} min={0} max={15} step={0.1} suffix="%" explainer={explainers.byId.get('interestRate')} onChange={(value) => setState((prev) => ({ ...prev, interestRate: value }))} />
            <NumberField id="compoundYears" label="Years to grow" value={state.years} min={1} max={60} step={1} parseMode="integer" explainer={explainers.byId.get('years')} onChange={(value) => setState((prev) => ({ ...prev, years: Math.round(value) }))} />
          </div>

          <div className="space-y-2 text-sm">
            <span className="flex items-center gap-1.5">
              <label className="text-sm font-semibold text-foreground" htmlFor="compound-frequency">
                Compounding frequency
              </label>
              {explainers.byId.get('compoundFrequency') ? (
                <FieldExplainer explainer={explainers.byId.get('compoundFrequency')!} />
              ) : null}
            </span>
            <Select
              id="compound-frequency"
              value={state.compoundFrequency}
              onChange={(event) => setState((prev) => ({ ...prev, compoundFrequency: event.target.value }))}
              options={compoundFrequencyOptions}
            />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Projected value" value={formatCurrency(result.finalBalance)} note={`after ${state.years} years`} trend="up" explainer={explainers.byId.get('finalBalance')} />
          <ResultSummaryCard label="Total contributions" value={formatCurrency(result.totalContributions)} note="principal + all deposits" trend="flat" explainer={explainers.byId.get('totalContributions')} />
          <ResultSummaryCard label="Total interest" value={formatCurrency(result.totalInterest)} note={`${formatPercent(growthRatio * 100)} of contributions`} trend={result.totalInterest >= 0 ? 'up' : 'down'} explainer={explainers.byId.get('totalInterest')} />
          <ResultSummaryCard label="Rule of 72" value={result.ruleOf72 ? `${result.ruleOf72.toFixed(1)} years` : 'N/A'} note="estimated doubling period" trend="flat" explainer={explainers.byId.get('ruleOf72')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'compoundInterest', shareState);
              const ok = await copyToClipboard(shareUrl);
              toast({
                title: ok ? 'Share link copied' : 'Share link ready',
                description: ok ? shareUrl : `Copy manually: ${shareUrl}`,
                variant: ok ? 'success' : 'warning'
              });
            }}
            onExportCsv={() => {
              const headers = ['Year', 'Start Balance', 'Contributions', 'Interest', 'End Balance'];
              const dataRows = result.yearlyData.map((row) => [
                row.year,
                Math.round(row.startBalance),
                Math.round(row.contributions),
                Math.round(row.interest),
                Math.round(row.endBalance)
              ]);
              exportRowsToCsv(headers, dataRows, 'compound-interest-projection.csv');
              toast({ title: 'CSV exported', description: 'compound-interest-projection.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Compound Interest Projection',
                subtitle: `${state.years} year horizon`,
                summaryLeft: [
                  `Starting principal: ${formatCurrency(state.principal)}`,
                  `Monthly contribution: ${formatCurrency(state.monthlyContribution)}`,
                  `Annual rate: ${state.interestRate.toFixed(1)}%`,
                  `Compounding frequency: ${state.compoundFrequency}`
                ],
                summaryRight: [
                  `Final balance: ${formatCurrency(result.finalBalance)}`,
                  `Contributions: ${formatCurrency(result.totalContributions)}`,
                  `Interest: ${formatCurrency(result.totalInterest)}`,
                  `Rule of 72: ${result.ruleOf72 ? `${result.ruleOf72.toFixed(1)} years` : 'N/A'}`
                ],
                headers: ['Year', 'Start', 'Contributions', 'Interest', 'End Balance'],
                rows: result.yearlyData.map((row) => [
                  row.year,
                  formatCurrency(row.startBalance),
                  formatCurrency(row.contributions),
                  formatCurrency(row.interest),
                  formatCurrency(row.endBalance)
                ]),
                filename: 'compound-interest-projection.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'compound-interest-projection.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Growth curves"
          description="End balance beside cumulative contributions and cumulative interest, so the three lines share one scale."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                End balance <span className="font-semibold text-foreground">{formatCurrency(result.finalBalance)}</span>
              </span>
              <span>
                Total interest <span className="font-semibold text-foreground">{formatCurrency(result.totalInterest)}</span>
              </span>
            </div>
          }
          legend={<ChartLegendPills config={chartConfig} keys={['endBalance', 'contributions', 'interest']} />}
        >
          <ChartContainer config={chartConfig} family="comparison">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={70}
                tickFormatter={(value) => formatCompactCurrency(Number(value))}
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="endBalance" type="natural" stroke="var(--color-endBalance)" strokeWidth={3.2} dot={false} />
              <Line dataKey="contributions" type="natural" stroke="var(--color-contributions)" strokeWidth={1.9} dot={false} />
              <Line dataKey="interest" type="natural" stroke="var(--color-interest)" strokeWidth={1.9} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Projection table" description="Complete yearly projection details.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Year</TableHead>
                <TableHead numeric>Start</TableHead>
                <TableHead numeric>Contributions</TableHead>
                <TableHead numeric>Interest</TableHead>
                <TableHead numeric>End Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.yearlyData.map((row) => (
                <TableRow key={row.year}>
                  <TableCell numeric>{row.year}</TableCell>
                  <TableCell numeric>{formatCurrency(row.startBalance)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.contributions)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.interest)}</TableCell>
                  <TableCell numeric>{formatCurrency(row.endBalance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="What every input and result on this page actually does to the math, worked through with the figures you entered. The info icon beside each field opens the same note in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
